import { type ReactElement, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyList from 'components/EmptyList/EmptyList';

import { MONITORING_AGENT_TYPES } from './scheduleWatchAgents';
import WatchTowerFoldPanel from './WatchTowerFoldPanel';
import WatchTowerIssueMap from './WatchTowerIssueMap';
import WatchTowerIssueTimeline from './WatchTowerIssueTimeline';

import type {
  IssueMapMarker,
  RunIssueCard,
  TimelineRange,
} from './runAnalysisSelectors';

import styles from './scss/TargetedMarketing.module.scss';

interface IWatchTowerIssueOverview {
  agentType: string,
  country: string,
  severity: string,
  markers: IssueMapMarker[],
  timelineDays: string[],
  timelineRange: TimelineRange,
  markCounts: Record<string, number>,
  severityRows: string[],
  severityAgentCounts: Record<string, number>,
  afterTimeline: ReactNode,
  emptyHeatText: string,
  mapPanelId: string,
  heatPanelId: string,
  onMapCountryClick: (country: string) => void,
  onTimelineRangeChange: (range: TimelineRange) => void,
  onTimelineMarkClick: (day: string) => void,
  onSeverityAgentClick: (severity: string, agentType: string) => void,
  onOpenIssue: (card: RunIssueCard) => void,
}

function severityClass(severity: string): string {
  if (severity === 'high' || severity === 'critical') {
    return styles.severityError;
  }
  if (severity === 'medium') {
    return styles.severityWarning;
  }
  return styles.muted;
}

function heatCellClass(selected: boolean): string {
  if (selected) {
    return `${styles.heatCell} ${styles.heatCellActive}`;
  }
  return styles.heatCell;
}

function WatchTowerIssueOverview({
  agentType,
  country,
  severity,
  markers,
  timelineDays,
  timelineRange,
  markCounts,
  severityRows,
  severityAgentCounts,
  afterTimeline,
  emptyHeatText,
  mapPanelId,
  heatPanelId,
  onMapCountryClick,
  onTimelineRangeChange,
  onTimelineMarkClick,
  onSeverityAgentClick,
  onOpenIssue,
}: IWatchTowerIssueOverview): ReactElement {
  const { t } = useTranslation();
  const [mapOpen, setMapOpen] = useState(true);
  const [heatOpen, setHeatOpen] = useState(true);

  return (
    <>
      <h3 className={styles.sectionTitle}>{t('targetedMarketing.timelineTitle')}</h3>
      <p className={styles.muted}>{t('targetedMarketing.timelineHint')}</p>
      <WatchTowerIssueTimeline
        days={timelineDays}
        range={timelineRange}
        markCounts={markCounts}
        onRangeChange={onTimelineRangeChange}
        onMarkClick={onTimelineMarkClick}
      />
      {afterTimeline}
      <WatchTowerFoldPanel
        id={mapPanelId}
        title={t('targetedMarketing.issueMapTitle')}
        open={mapOpen}
        onToggle={() => setMapOpen((open) => !open)}
      >
        <p className={styles.muted}>{t('targetedMarketing.issueMapHint')}</p>
        <WatchTowerIssueMap
          markers={markers}
          selectedCountry={country}
          onSelectCountry={onMapCountryClick}
          onOpenIssue={onOpenIssue}
        />
      </WatchTowerFoldPanel>
      <WatchTowerFoldPanel
        id={heatPanelId}
        title={t('targetedMarketing.issueTimeTitle')}
        open={heatOpen}
        onToggle={() => setHeatOpen((open) => !open)}
      >
        {severityRows.length === 0 ? (
          <EmptyList text={emptyHeatText} />
        ) : (
          <div className={styles.tableWrap}>
            <table className={`${styles.table} ${styles.tableCompact}`}>
              <thead>
                <tr>
                  <th className={styles.stickyCol}>{t('targetedMarketing.severity')}</th>
                  {MONITORING_AGENT_TYPES.map((item) => (
                    <th key={item.type}>{item.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {severityRows.map((rowSeverity) => (
                  <tr key={rowSeverity}>
                    <td className={`${styles.stickyCol} ${severityClass(rowSeverity)}`}>
                      {rowSeverity}
                    </td>
                    {MONITORING_AGENT_TYPES.map((item) => {
                      const count = severityAgentCounts[`${rowSeverity}|${item.type}`] || 0;
                      const selected = severity === rowSeverity && agentType === item.type;
                      if (count === 0) {
                        return (
                          <td key={item.type}>
                            <span className={styles.muted}>0</span>
                          </td>
                        );
                      }
                      return (
                        <td key={item.type}>
                          <button
                            type="button"
                            className={heatCellClass(selected)}
                            onClick={() => onSeverityAgentClick(rowSeverity, item.type)}
                          >
                            {count}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </WatchTowerFoldPanel>
    </>
  );
}

export default WatchTowerIssueOverview;
